import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getDeliveryChallans = createAsyncThunk(
  "deliveryChallan/getDeliveryChallans",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.DELIVERY_CHALLAN.SLUG}?${queryString}`,
      );
      const dc_list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { dc_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getPendingDCSalesOrders = createAsyncThunk(
  "deliveryChallan/getPendingDCSalesOrders",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setPendingSOActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.DELIVERY_CHALLAN.PENDING_SALES_ORDERS}?${queryString}`,
      );
      const list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getDeliveryChallanById = createAsyncThunk(
  "deliveryChallan/getDeliveryChallanById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.DELIVERY_CHALLAN.SLUG}/${id}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const createDeliveryChallan = createAsyncThunk(
  "deliveryChallan/createDeliveryChallan",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.post(API_URL.DELIVERY_CHALLAN.SLUG, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const updateDeliveryChallan = createAsyncThunk(
  "deliveryChallan/updateDeliveryChallan",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.DELIVERY_CHALLAN.SLUG}/${payload.id}`,
        payload,
      );
      const { active_payload } = getState().deliveryChallan;
      dispatch(getDeliveryChallans(active_payload));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const confirmDeliveryChallan = createAsyncThunk(
  "deliveryChallan/confirmDeliveryChallan",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(API_URL.DELIVERY_CHALLAN.CONFIRM(id));
      const { active_payload } = getState().deliveryChallan;
      if (active_payload) {
        dispatch(getDeliveryChallans(active_payload));
      }
      await dispatch(getDeliveryChallanById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const cancelDeliveryChallan = createAsyncThunk(
  "deliveryChallan/cancelDeliveryChallan",
  async ({ id, reason }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(API_URL.DELIVERY_CHALLAN.CANCEL(id), {
        reason,
      });
      const { active_payload } = getState().deliveryChallan;
      if (active_payload) {
        dispatch(getDeliveryChallans(active_payload));
      }
      await dispatch(getDeliveryChallanById(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getSalesOrdersForDC = createAsyncThunk(
  "deliveryChallan/getSalesOrdersForDC",
  async (customerId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.DELIVERY_CHALLAN.SALES_ORDERS_BY_CUSTOMER(customerId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getEligiblePackagesForDC = createAsyncThunk(
  "deliveryChallan/getEligiblePackagesForDC",
  async ({ salesOrderIds, excludeDCId = null }, { rejectWithValue }) => {
    try {
      // Support both single ID (backward compat) and array of IDs
      const ids = Array.isArray(salesOrderIds)
        ? salesOrderIds
        : [salesOrderIds];
      const responses = await Promise.all(
        ids.map((soId) =>
          axios.get(
            API_URL.DELIVERY_CHALLAN.ELIGIBLE_PACKAGES(soId, excludeDCId),
          ),
        ),
      );
      // Merge results, tag each package with its salesOrderId for grouping
      const allPackages = [];
      responses.forEach((res, idx) => {
        const packages = res.data.data || [];
        packages.forEach((pkg) => {
          allPackages.push({ ...pkg, salesOrderId: ids[idx] });
        });
      });
      return allPackages;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getWarehousesForDC = createAsyncThunk(
  "deliveryChallan/getWarehousesForDC",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.WAREHOUSE.LOOKUP);
      return data.data?.list || data.data || [];
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getDCCreationEligibility = createAsyncThunk(
  "deliveryChallan/getDCCreationEligibility",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.DELIVERY_CHALLAN.CREATION_ELIGIBILITY(salesOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

const initialState = {
  dc_list: [],
  dc_detail: null,
  detail_loading: false,
  current_values: null,
  active_payload: null,
  isLoading: false,
  pending_dc_so_list: [],
  pending_dc_so_total_records: 0,
  pending_dc_so_active_payload: null,
  sales_orders_for_dc: [],
  sales_orders_for_dc_loading: false,
  eligible_packages: [],
  eligible_packages_loading: false,
  dc_creation_eligibility: null,
  dc_creation_eligibility_loading: false,
  warehouses: [],
  warehouses_loading: false,
};

const deliveryChallanSlice = createSlice({
  name: "deliveryChallan",
  initialState,
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    setPendingSOActivePayload(state, action) {
      state.pending_dc_so_active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.dc_list = [];
    },
    resetSalesOrdersForDC(state) {
      state.sales_orders_for_dc = [];
    },
    resetEligiblePackages(state) {
      state.eligible_packages = [];
    },
    resetDCCreationEligibility(state) {
      state.dc_creation_eligibility = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDeliveryChallans.fulfilled, (state, action) => {
        state.dc_list = action.payload.dc_list;
      })
      // Pending DC Sales Orders
      .addCase(getPendingDCSalesOrders.fulfilled, (state, action) => {
        state.pending_dc_so_list = action.payload.list;
        state.pending_dc_so_total_records = action.payload.data.totalRecords;
      })
      .addCase(getDeliveryChallanById.pending, (state) => {
        state.detail_loading = true;
      })
      .addCase(getDeliveryChallanById.fulfilled, (state, action) => {
        state.dc_detail = action.payload;
        state.detail_loading = false;
      })
      .addCase(getDeliveryChallanById.rejected, (state) => {
        state.detail_loading = false;
      })
      // Sales Orders for DC
      .addCase(getSalesOrdersForDC.pending, (state) => {
        state.sales_orders_for_dc_loading = true;
      })
      .addCase(getSalesOrdersForDC.fulfilled, (state, action) => {
        state.sales_orders_for_dc = action.payload;
        state.sales_orders_for_dc_loading = false;
      })
      .addCase(getSalesOrdersForDC.rejected, (state) => {
        state.sales_orders_for_dc = [];
        state.sales_orders_for_dc_loading = false;
      })
      // Eligible Packages
      .addCase(getEligiblePackagesForDC.pending, (state) => {
        state.eligible_packages_loading = true;
      })
      .addCase(getEligiblePackagesForDC.fulfilled, (state, action) => {
        state.eligible_packages = action.payload;
        state.eligible_packages_loading = false;
      })
      .addCase(getEligiblePackagesForDC.rejected, (state) => {
        state.eligible_packages = [];
        state.eligible_packages_loading = false;
      })
      // DC Creation Eligibility
      .addCase(getDCCreationEligibility.pending, (state) => {
        state.dc_creation_eligibility_loading = true;
      })
      .addCase(getDCCreationEligibility.fulfilled, (state, action) => {
        state.dc_creation_eligibility = action.payload;
        state.dc_creation_eligibility_loading = false;
      })
      .addCase(getDCCreationEligibility.rejected, (state) => {
        state.dc_creation_eligibility = null;
        state.dc_creation_eligibility_loading = false;
      })
      // Warehouses
      .addCase(getWarehousesForDC.pending, (state) => {
        state.warehouses_loading = true;
      })
      .addCase(getWarehousesForDC.fulfilled, (state, action) => {
        state.warehouses = action.payload;
        state.warehouses_loading = false;
      })
      .addCase(getWarehousesForDC.rejected, (state) => {
        state.warehouses = [];
        state.warehouses_loading = false;
      });
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  setPendingSOActivePayload,
  resetCurrentValue,
  resetSalesOrdersForDC,
  resetEligiblePackages,
  resetDCCreationEligibility,
} = deliveryChallanSlice.actions;
export default deliveryChallanSlice.reducer;
